<template>
  <div class="msg-panel">
    <!-- Thread view -->
    <div v-if="view === 'thread' && activeThread" class="msg-thread">
      <div class="msg-thread-head">
        <button class="msg-back" @click="backToList"><i class="fas fa-chevron-left"></i></button>
        <span class="msg-avatar msg-avatar-sm">
          <img v-if="activeThread.avatar_url" :src="activeThread.avatar_url" :alt="activeThread.nick_name" class="msg-avatar-img" />
          <template v-else>{{ initial(activeThread.nick_name) }}</template>
        </span>
        <span class="msg-thread-name">{{ activeThread.nick_name }}</span>
      </div>
      <!-- Coven reciprocity: they put me in their coven — one tap to add them back. -->
      <div v-if="threadMeta.they_covened_me && !threadMeta.i_covened_them" class="msg-coven-cta">
        <span class="msg-coven-text">✦ {{ activeThread.nick_name }} added you to their coven.</span>
        <button class="msg-coven-btn" :disabled="coveningBack" @click="addToCoven">
          {{ coveningBack ? 'Adding…' : `Add ${activeThread.nick_name} to my Coven` }}
        </button>
      </div>
      <div v-else-if="threadMeta.they_covened_me && threadMeta.i_covened_them" class="msg-coven-done">
        ✦ You're in each other's coven.
      </div>
      <div class="msg-bubbles" ref="bubbles">
        <div v-for="m in threadMessages" :key="m.id" class="msg-bubble" :class="{ mine: m.from_me }">
          <p v-if="m.body" class="msg-text" style="white-space: pre-line">{{ m.body }}</p>
          <a
            v-if="readingLink(m)" class="msg-reading"
            :href="readingLink(m).href"
          ><i class="fas" :class="readingLink(m).icon"></i> {{ readingLink(m).label }}</a>
        </div>
        <p v-if="!threadMessages.length" class="msg-empty">Say hello ✦</p>
      </div>
      <div class="msg-compose">
        <input
          v-model="draft" class="msg-input" placeholder="Message…"
          @keyup.enter="send"
        />
        <button class="msg-send" :disabled="!draft.trim() || sending" @click="send">
          <i class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>

    <!-- Compose view: pick a friend to message -->
    <div v-else-if="view === 'compose'" class="msg-list">
      <div class="msg-thread-head">
        <button class="msg-back" @click="view = 'list'"><i class="fas fa-chevron-left"></i></button>
        <span class="msg-thread-name">New message</span>
      </div>
      <div class="msg-section">
        <div class="msg-section-label">Choose a friend</div>
        <button v-for="f in friends" :key="f.querent_uuid" class="msg-conv" @click="openThread(f)">
          <span class="msg-avatar">
            <img v-if="f.avatar_url" :src="f.avatar_url" :alt="f.nick_name" class="msg-avatar-img" />
            <template v-else>{{ initial(f.nick_name) }}</template>
          </span>
          <span class="msg-conv-body"><span class="msg-conv-name">{{ f.nick_name }}</span></span>
        </button>
      </div>
    </div>

    <!-- List view: requests + conversations -->
    <div v-else class="msg-list">
      <div class="msg-listhead">
        <span class="msg-section-label" style="margin:0">Messages</span>
        <button v-if="friends.length" class="msg-new" @click="openCompose">
          <i class="fas fa-pen"></i> New
        </button>
      </div>

      <div v-if="requests.length" class="msg-section">
        <div class="msg-section-label">Friend requests</div>
        <div v-for="r in requests" :key="r.querent_uuid" class="msg-request">
          <span class="msg-avatar">
            <img v-if="r.avatar_url" :src="r.avatar_url" :alt="r.nick_name" class="msg-avatar-img" />
            <template v-else>{{ initial(r.nick_name) }}</template>
          </span>
          <template v-if="acceptingId === r.querent_uuid">
            <input
              v-model="aliasDraft" class="msg-alias-input" :placeholder="`Nickname for ${r.nick_name} (optional)`"
              @keyup.enter="confirmAccept(r)" ref="aliasInputEl"
            />
            <button class="msg-accept" @click="confirmAccept(r)">Confirm</button>
            <button class="msg-reject" @click="acceptingId = null">Cancel</button>
          </template>
          <template v-else>
            <span class="msg-request-name">{{ r.nick_name }}</span>
            <button class="msg-accept" @click="startAccept(r)">Accept</button>
            <button class="msg-reject" @click="reject(r)">Decline</button>
          </template>
        </div>
      </div>

      <div class="msg-section">
        <p v-if="!conversations.length" class="msg-empty">{{ emptyMessage }}</p>
        <button
          v-for="c in conversations" :key="c.querent_uuid" class="msg-conv"
          @click="openThread(c)"
        >
          <span class="msg-avatar">
            <img v-if="c.avatar_url" :src="c.avatar_url" :alt="c.nick_name" class="msg-avatar-img" />
            <template v-else>{{ initial(c.nick_name) }}</template>
          </span>
          <span class="msg-conv-body">
            <span class="msg-conv-name">{{ c.nick_name }}</span>
            <span class="msg-conv-last">{{ c.last_message }}</span>
          </span>
          <span v-if="c.unread" class="msg-unread">{{ c.unread }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * MessagesPanel — the Altar's Messages tab. Surfaces incoming FRIEND REQUESTS
 * (accept w/ optional alias, or decline) and friend DM conversations (open a
 * thread, send a message). Shared readings arrive here as message attachments.
 */
import { ref, computed, onMounted, nextTick } from 'vue'
import { store } from '@/store'
import { messageReadingLink as readingLink } from '@/utils/message-reading-link'
import {
  getFriendRequests, respondFriendRequest, getConversations, getMessageThread, sendMessage, getMyFriends, setFriendTier,
} from '@/api-service'

const requests = ref([])
const conversations = ref([])
const friends = ref([])
const view = ref('list')

// Smart empty state: no friends vs friends-but-no-chats.
const emptyMessage = computed(() => friends.value.length
  ? 'No conversations yet. Tap New to message a friend.'
  : "You haven't connected with anyone yet. Find seekers in the Community to add friends, then message them here.")
const activeThread = ref(null)
const threadMessages = ref([])
const threadMeta = ref({})        // coven reciprocity + avatar from the thread payload
const coveningBack = ref(false)
const draft = ref('')
const sending = ref(false)
const bubbles = ref(null)
const acceptingId = ref(null)   // which request is showing its inline alias input
const aliasDraft = ref('')
const aliasInputEl = ref(null)

const initial = (n) => (n || '?').charAt(0).toUpperCase()

async function loadList() {
  try { requests.value = (await getFriendRequests(store.session_id)).requests || [] } catch (e) { /* */ }
  try { conversations.value = (await getConversations(store.session_id)).conversations || [] } catch (e) { /* */ }
  try { friends.value = (await getMyFriends(store.session_id)).friends || [] } catch (e) { /* */ }
}

async function openCompose() {
  try { friends.value = (await getMyFriends(store.session_id)).friends || [] } catch (e) { /* */ }
  view.value = 'compose'
}

function startAccept(r) {
  acceptingId.value = r.querent_uuid
  aliasDraft.value = ''
  nextTick(() => { if (aliasInputEl.value) (aliasInputEl.value[0] || aliasInputEl.value).focus() })
}
async function confirmAccept(r) {
  try { await respondFriendRequest(r.querent_uuid, 'accept', store.session_id, aliasDraft.value.trim()) } catch (e) { /* */ }
  acceptingId.value = null
  window.dispatchEvent(new CustomEvent('messages-refresh'))
  await loadList()
}
async function reject(r) {
  try { await respondFriendRequest(r.querent_uuid, 'reject', store.session_id) } catch (e) { /* */ }
  await loadList()
}

async function openThread(c) {
  activeThread.value = { querent_uuid: c.querent_uuid, nick_name: c.nick_name, avatar_url: c.avatar_url || '' }
  threadMeta.value = {}
  view.value = 'thread'
  await loadThread()
}
async function loadThread() {
  try {
    const data = await getMessageThread(activeThread.value.querent_uuid, store.session_id)
    threadMessages.value = data.messages || []
    threadMeta.value = data
    if (data.friend_nick_name) activeThread.value.nick_name = data.friend_nick_name
    if (data.avatar_url) activeThread.value.avatar_url = data.avatar_url
    await nextTick()
    if (bubbles.value) bubbles.value.scrollTop = bubbles.value.scrollHeight
  } catch (e) { /* */ }
}
// Reciprocate: add the person who covened me back into MY coven.
async function addToCoven() {
  if (coveningBack.value) return
  coveningBack.value = true
  try {
    const res = await setFriendTier(activeThread.value.querent_uuid, 'coven', store.session_id)
    if (res && res.reason === 'coven_full') {
      // A full coven (coven design 2026-10-26): the one swap UI is the Circle
      // tab, so open it with this person waiting in the middle of the ring.
      window.dispatchEvent(new CustomEvent('open-circle', { detail: { incoming: activeThread.value.querent_uuid } }))
      return
    }
    if (res && res.ok === false) return
    threadMeta.value = { ...threadMeta.value, i_covened_them: true }
  } catch (e) { /* */ } finally { coveningBack.value = false }
}
async function send() {
  const body = draft.value.trim()
  if (!body || sending.value) return
  sending.value = true
  try {
    await sendMessage(activeThread.value.querent_uuid, body, store.session_id)
    draft.value = ''
    await loadThread()
  } catch (e) { /* */ } finally { sending.value = false }
}
function backToList() { view.value = 'list'; activeThread.value = null; loadList() }

onMounted(loadList)
</script>

<style scoped>
.msg-panel { color: #e9dcff; font-family: inherit; height: 100%; display: flex; flex-direction: column; min-height: 0; }
.msg-section { margin-bottom: 18px; }
.msg-section-label { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #9b86c9; margin: 0 0 8px; }
.msg-listhead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.msg-new { display: inline-flex; align-items: center; gap: 6px; background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #fff; border: none; border-radius: 999px; padding: 7px 14px; font-size: 12.5px; font-weight: 700;
  cursor: pointer; font-family: inherit; }
.msg-new:hover { filter: brightness(1.08); }
.msg-empty { font-size: 13px; color: #9b86c9; font-style: italic; line-height: 1.5; }
.msg-avatar { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 50%; overflow: hidden; background: linear-gradient(160deg, #5d3b8c, #2a1845); color: #f0d9a3; font-weight: 700; font-size: 14px; }
.msg-avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.msg-avatar-sm { width: 30px; height: 30px; font-size: 13px; margin-right: 4px; }

/* Coven reciprocity CTA in a thread. */
.msg-coven-cta { display: flex; flex-direction: column; gap: 8px; margin: 0 0 12px; padding: 12px 14px;
  background: rgba(123,75,176,0.16); border: 1px solid rgba(195,169,255,0.28); border-radius: 12px; }
.msg-coven-text { font-size: 13px; color: #e9dcff; }
.msg-coven-btn { align-self: flex-start; background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none;
  border-radius: 999px; padding: 8px 16px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit; }
.msg-coven-btn:hover { filter: brightness(1.08); }
.msg-coven-btn:disabled { opacity: 0.6; cursor: default; }
.msg-coven-done { margin: 0 0 12px; padding: 8px 14px; font-size: 12px; color: #b9a6e6; text-align: center;
  background: rgba(123,75,176,0.08); border-radius: 10px; }

/* Friend requests */
.msg-request { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid rgba(255,255,255,0.06); }
.msg-request-name { flex: 1; font-size: 14px; font-weight: 600; }
.msg-accept { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none; border-radius: 999px; padding: 6px 14px; font-size: 12px; font-weight: 700; cursor: pointer; }
.msg-reject { background: transparent; color: #9b86c9; border: 1px solid rgba(155,134,201,0.4); border-radius: 999px; padding: 6px 12px; font-size: 12px; cursor: pointer; }
.msg-alias-input { flex: 1; min-width: 0; padding: 7px 12px; border-radius: 999px; border: 1px solid rgba(155,134,201,0.5); background: rgba(20,8,40,0.55); color: #e9dcff; font-size: 13px; outline: none; }
.msg-alias-input::placeholder { color: #9b86c9; }

/* Conversations */
.msg-conv { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px 8px; background: transparent; border: none; border-radius: 10px; cursor: pointer; text-align: left; transition: background 0.15s; }
.msg-conv:hover { background: rgba(93,59,140,0.18); }
.msg-conv-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.msg-conv-name { font-size: 14px; font-weight: 600; color: #e9dcff; }
.msg-conv-last { font-size: 12.5px; color: #9b86c9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.msg-unread { flex-shrink: 0; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px; background: #C3A9FF; color: #1c1030; font-size: 11px; font-weight: 800; display: flex; align-items: center; justify-content: center; }

/* Thread */
.msg-thread { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.msg-thread-head { display: flex; align-items: center; gap: 10px; padding-bottom: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); }
.msg-back { background: transparent; border: none; color: #C3A9FF; font-size: 16px; cursor: pointer; }
.msg-thread-name { font-size: 15px; font-weight: 700; }
.msg-bubbles { flex: 1; overflow-y: auto; padding: 14px 2px; display: flex; flex-direction: column; gap: 8px; min-height: 0; }
.msg-bubble { max-width: 78%; padding: 9px 13px; border-radius: 14px; background: rgba(93,59,140,0.3); align-self: flex-start; }
.msg-bubble.mine { align-self: flex-end; background: linear-gradient(180deg, #7b4bb0, #4e2776); }
.msg-text { margin: 0; font-size: 14px; line-height: 1.4; }
.msg-reading { display: inline-flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 12.5px; color: #f0d9a3; text-decoration: none; }
.msg-compose { display: flex; gap: 8px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.08); }
.msg-input { flex: 1; padding: 10px 14px; border-radius: 999px; border: 1px solid rgba(155,134,201,0.4); background: rgba(20,8,40,0.6); color: #e9dcff; outline: none; font-size: 14px; }
.msg-send { flex-shrink: 0; width: 40px; border: none; border-radius: 50%; background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; cursor: pointer; }
.msg-send:disabled { opacity: 0.5; cursor: default; }
</style>
